/**
 * Currency conversion and formatting utilities
 */

// Exchange rate: 1 EUR = 1.95583 BGN (fixed rate)
const BGN_TO_EUR = 1.95583;

/**
 * Convert BGN to EUR
 * @param {number} bgnAmount - Amount in BGN
 * @returns {number} Amount in EUR
 */
export const convertBgnToEur = (bgnAmount) => {
  return bgnAmount / BGN_TO_EUR;
};

/**
 * Format price with both BGN and EUR
 * @param {string|number} price - Price in BGN (can be "100лв", "100 BGN", "40лв/кв.м" or 100)
 * @returns {string} Formatted price "100лв / 51€"
 */
export const formatPriceWithEur = (price) => {
  // If already contains EUR, return as is
  if (typeof price === 'string' && price.includes('€')) {
    return price;
  }

  // Check for per sqm suffix
  const hasPerSqm = typeof price === 'string' && price.includes('/кв.м');
  const suffix = hasPerSqm ? ' на кв.м' : '';

  // Extract numeric value from price string
  let numericPrice;

  if (typeof price === 'string') {
    // Remove "лв", "BGN", "/кв.м", spaces and parse - keep only number
    numericPrice = parseFloat(price.replace(/[^\d.]/g, ''));
  } else {
    numericPrice = price;
  }

  // Return original if not a valid number
  if (isNaN(numericPrice)) {
    return price;
  }

  // Convert to EUR
  const eurPrice = convertBgnToEur(numericPrice);

  // Check if original was BGN or лв
  const isBGN = typeof price === 'string' && price.includes('BGN');
  const bgnLabel = isBGN ? ' BGN' : 'лв';

  // Format both prices
  return `${numericPrice.toFixed(0)}${bgnLabel} / ${eurPrice.toFixed(0)}€${suffix}`;
};

/**
 * Format a single price number with both currencies
 * @param {number} bgnAmount - Amount in BGN
 * @returns {string} Formatted price "100лв / 51€"
 */
export const formatAmountWithEur = (bgnAmount) => {
  const eurAmount = convertBgnToEur(bgnAmount);
  return `${bgnAmount.toFixed(0)}лв / ${eurAmount.toFixed(0)}€`;
};

/**
 * Format prices within text (e.g., "Price is 200лв" -> "Price is 200лв / 102€")
 * @param {string} text - Text containing prices
 * @returns {string} Text with formatted prices
 */
export const formatPricesInText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Match patterns like "200лв", "+50лв", "200 BGN", "+50 BGN"
  return text.replace(/(\+?\d+)\s?(лв|BGN)/g, (match, amount, currency) => {
    const numAmount = parseInt(amount.replace('+', ''));
    const eurAmount = convertBgnToEur(numAmount);
    const prefix = amount.startsWith('+') ? '+' : '';
    const currencyLabel = currency === 'BGN' ? ' BGN' : 'лв';
    return `${prefix}${numAmount}${currencyLabel} / ${eurAmount.toFixed(0)}€`;
  });
};
